import React from "react";
import { jsPDF } from "jspdf";
import { IconButton, Button } from "@mui/material";
import { PictureAsPdfSharp as PictureAsPdfSharpIcon } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import EmailIcon from '@mui/icons-material/Email';
import { showModal, hideModal } from "../../data/modal";
import { TerminalCall } from "../../types/TerminalCall.ts";
import { v4 as uuid } from "uuid";
import dayjs from "dayjs";

export const DOCUMENT_ORIENTATION_PORTRAIT = "portrait";
export const DOCUMENT_ORIENTATION_LANDSCAPE = "landscape";
const PLAN_TYPE_LOADING = "loadingTerminalCall";
const PLAN_TYPE_LOADING_ONLY = "loading_only";

const DOCUMENT_ORIENTATIONS = {
  portrait: "p",
  landscape: "l",
};

export const createPdf = (orientation: string) => {
  return new jsPDF(DOCUMENT_ORIENTATIONS[orientation], "mm", "a4", true);
};

interface GeneratePDFProps {
  filename?: string;
  orientation?: string;
  variant?: "icon" | "button";
  containerRef?: React.RefObject<HTMLElement>;
  terminalCall?: TerminalCall;
}

const GeneratePDF: React.FC<GeneratePDFProps> = ({
  filename,
  orientation = DOCUMENT_ORIENTATION_PORTRAIT,
  variant = "icon",
  containerRef,
  terminalCall,
  vessel,
  planType,
}) => {
  const dispatch = useDispatch();
  const contacts = terminalCall?.terminal?.contacts

  const handleClick = () => {
    savePdf()
  }

  const savePdf = () => {
    const htmlContainer = containerRef?.current;

    if (!htmlContainer) {
      console.warn("PDF container not found.");
      return;
    }

    const pdf = createPdf(orientation);

    const padding = 10;
    const pdfPageWidth = pdf.internal.pageSize.getWidth();
    const pdfPageHeight = pdf.internal.pageSize.getHeight();

    const contentWidth = htmlContainer.scrollWidth;
    const contentHeight = htmlContainer.scrollHeight;

    const availableWidth = pdfPageWidth - padding * 2;
    const availableHeight = pdfPageHeight - padding * 2;

    const scaleX = availableWidth / contentWidth;
    const scaleY = availableHeight / contentHeight;
    const scale = Math.min(scaleX, scaleY);

    const scaledWidth = contentWidth * scale;
    const scaledHeight = contentHeight * scale;

    const offsetX = (pdfPageWidth - scaledWidth) / 2;
    const offsetY = (pdfPageHeight - scaledHeight) / 2;

    pdf.html(htmlContainer, {
      html2canvas: {
        width: contentWidth,
        height: contentHeight,
        scale,
        scrollX: -window.scrollX,
        scrollY: -window.scrollY,
      },
      callback(pdf) {
        pdf.save(`${filename ?? uuid()}.pdf`);
      },
      x: offsetX,
      y: offsetY,
    });
  };

  const mailPlanning = async (data) => {
    const contact = data.contact

    if (!contact) { return; }

    const htmlContainer = containerRef?.current;

    if (!htmlContainer) {
      console.warn("PDF container not found.");
      return;
    }

    const pdf = createPdf(DOCUMENT_ORIENTATION_PORTRAIT);

    const padding = 10;
    const pdfPageWidth = pdf.internal.pageSize.getWidth();
    const pdfPageHeight = pdf.internal.pageSize.getHeight();

    const contentWidth = htmlContainer.scrollWidth;
    const contentHeight = htmlContainer.scrollHeight;

    const availableWidth = pdfPageWidth - padding * 2;
    const availableHeight = pdfPageHeight - padding * 2;

    const scaleX = availableWidth / contentWidth;
    const scaleY = availableHeight / contentHeight;
    const scale = Math.min(scaleX, scaleY);

    const scaledWidth = contentWidth * scale;
    const scaledHeight = contentHeight * scale;

    const offsetX = (pdfPageWidth - scaledWidth) / 2;
    const offsetY = (pdfPageHeight - scaledHeight) / 2;

    pdf.html(htmlContainer, {
      html2canvas: {
        width: contentWidth,
        height: contentHeight,
        scale,
        scrollX: -window.scrollX,
        scrollY: -window.scrollY,
      },
      callback: async (pdf) => {
        const savedDocument = new Blob([pdf.output("blob")], {
          type: "application/pdf",
        });
        const formData = new FormData();

        formData.append(
          "file",
          savedDocument,
          `${uuid()}.pdf`
        );

        const result = await fetch(`${process.env.REACT_APP_API_URL}/files`, {
          method: "POST",
          body: formData,
          headers: {
            "Authorization": `Bearer ${localStorage.getItem("token")}`
          },
        });

        const data = await result.json();
        let fileID = data["@id"];
        let subject = `${vessel.name} - ${(planType === PLAN_TYPE_LOADING || planType === PLAN_TYPE_LOADING_ONLY) ? "Loading" : "Unloading"} - ${terminalCall.terminal.name} - ${dayjs(terminalCall?.planned).format("DD-MM-YYYY")}`;

        if (fileID) {
          //{subject}/{contact}/{file}/{terminalCall}/{vessel}/{sender}
          const response = await fetch(`${process.env.REACT_APP_API_URL}/email_planning/${subject}${contact.replace('api/contacts/', '')}${fileID.replace('api/files/', '')}${terminalCall["@id"].replace('api/terminal_calls/', '')}${vessel["@id"].replace('api/vessels/', '')}`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${localStorage.getItem("token")}`
              },
            })

          if (response) {
            dispatch(hideModal())
          }
        }
      },
      x: offsetX,
      y: offsetY,
    });
  };

  return (
    <>
      {variant === "button" ? (
        <Button
          onClick={handleClick}
          startIcon={<PictureAsPdfSharpIcon />}
          variant="outlined"
        >
          Generate PDF
        </Button>
      ) : (
        <IconButton onClick={handleClick}>
          <PictureAsPdfSharpIcon />
        </IconButton>
      )}
      {
        terminalCall &&
        <Button onClick={(e) => {
          dispatch(showModal({
            title: "Mail planning",
            description: "Mail loading/unloading planning to terminal",
            form: "terminalcallmailer",
            formProps: { mailPlanning, contacts },
          }));
        }}><EmailIcon fontSize="inherit" /></Button>
      }
    </>
  )
};

export default GeneratePDF;